import { useEffect, useMemo, useState } from "react";
import { FiDownload, FiEye, FiSearch } from "react-icons/fi";
import api from "../services/api";
import NetworkSummary from "../components/dashboard/NetworkSummary";
import IncentiveSummary from "../components/dashboard/IncentiveSummary";

const PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];

const Network = () => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [paymentDateFrom, setPaymentDateFrom] = useState("");
  const [paymentDateTo, setPaymentDateTo] = useState("");
  const [purchaseStatusFilter, setPurchaseStatusFilter] = useState("");
  const [accountStatusFilter, setAccountStatusFilter] = useState("");
  const [commissionValidity, setCommissionValidity] = useState("");
  const [statusOptions, setStatusOptions] = useState([]);
  const [accountStatusOptions, setAccountStatusOptions] = useState([]);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [exportError, setExportError] = useState("");
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);

  const totalPages = useMemo(() => {
    return lastPage || Math.max(1, Math.ceil(total / perPage));
  }, [lastPage, total, perPage]);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          return;
        }

        const [purchaseResponse, accountResponse] = await Promise.all([
          api.get("/product-purchase-statuses", {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
          api.get("/user-account-statuses", {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
        ]);

        if (purchaseResponse.data?.status === "success") {
          setStatusOptions(purchaseResponse.data?.data || []);
        }
        if (accountResponse.data?.status === "success") {
          setAccountStatusOptions(accountResponse.data?.data || []);
        }
      } catch (error) {
        return;
      }
    };

    fetchStatuses();
  }, []);

  useEffect(() => {
    const fetchNetwork = async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          setLoadError("Missing access token.");
          return;
        }

        const response = await api.get("/network", {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            search: query,
            level: levelFilter || undefined,
            payment_date_from: paymentDateFrom || undefined,
            payment_date_to: paymentDateTo || undefined,
            purchase_status_id: purchaseStatusFilter || undefined,
            account_status_id: accountStatusFilter || undefined,
            commission_validity: commissionValidity || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            page,
            per_page: perPage,
          },
        });

        if (response.data?.status !== "success") {
          setLoadError("Failed to load network data.");
          return;
        }

        const data = response.data?.data || {};
        setRows(data.rows || []);
        setTotal(data.total || 0);
        setLastPage(data.last_page || 1);
      } catch (error) {
        setLoadError("Failed to load network data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchNetwork();
  }, [
    query,
    levelFilter,
    paymentDateFrom,
    paymentDateTo,
    purchaseStatusFilter,
    accountStatusFilter,
    commissionValidity,
    sortBy,
    sortDir,
    page,
    perPage,
  ]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
  };

  const handleExportExcel = async () => {
    setExportError("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setExportError("Missing access token.");
        return;
      }

      const response = await api.get("/network/export", {
        headers: { Authorization: `Bearer ${token}` },
          params: {
            search: query,
            level: levelFilter || undefined,
            payment_date_from: paymentDateFrom || undefined,
            payment_date_to: paymentDateTo || undefined,
            purchase_status_id: purchaseStatusFilter || undefined,
            account_status_id: accountStatusFilter || undefined,
            commission_validity: commissionValidity || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
          },
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/vnd.ms-excel",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename=\"?([^\";]+)\"?/i);
      const fallbackName = "network-export.csv";
      link.href = url;
      link.download = match?.[1] || fallbackName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setExportError("Failed to export network data.");
    }
  };

  const formatDateTime = (value) => {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return date.toLocaleString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatAmount = (value) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) {
      return "₱ 0.00";
    }
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
      currencyDisplay: "symbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(amount)
      .replace("₱", "₱ ");
  };

  const getAccountStatusClass = (value) => {
    const normalized = String(value || "").toLowerCase();
    if (normalized === "pre registered") {
      return "bg-orange-100 text-orange-700";
    }
    if (normalized === "for validation") {
      return "bg-amber-100 text-amber-700";
    }
    if (normalized === "for activation") {
      return "bg-blue-100 text-blue-700";
    }
    if (normalized === "active") {
      return "bg-emerald-100 text-emerald-700";
    }
    if (normalized === "inactive") {
      return "bg-rose-100 text-rose-700";
    }
    if (normalized === "expired") {
      return "bg-red-100 text-red-700";
    }
    return "bg-slate-100 text-slate-600";
  };

  const getPurchaseStatusClass = (value) => {
    const normalized = String(value || "").toLowerCase();
    if (normalized === "for validation") {
      return "bg-amber-100 text-amber-700";
    }
    if (normalized === "approved") {
      return "bg-emerald-100 text-emerald-700";
    }
    if (normalized === "rejected") {
      return "bg-rose-100 text-rose-700";
    }
    if (normalized === "cancelled" || normalized === "canceled") {
      return "bg-red-100 text-red-700";
    }
    return "bg-slate-100 text-slate-600";
  };

  const renderPages = () => {
    const buttons = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + 4);
    for (let p = start; p <= end; p += 1) {
      buttons.push(
        <button
          key={p}
          type="button"
          onClick={() => setPage(p)}
          className={`min-w-[40px] rounded-xl border px-3 py-2 text-sm font-semibold ${
            p === page
              ? "border-[var(--brand-orange)] bg-orange-50 text-[var(--brand-orange-dark)]"
              : "border-slate-200 text-slate-600 hover:border-slate-300"
          }`}
        >
          {p}
        </button>
      );
    }
    return buttons;
  };

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
          Network
        </p>
        <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">
              Member Activity Overview
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Track member purchases, account status, and referral-level incentives.
            </p>
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
          >
            <FiDownload />
            Export Summary
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <NetworkSummary />
        <IncentiveSummary />
      </div>

      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <form onSubmit={handleSearchSubmit} className="relative flex-1 min-w-[220px]">
              <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </form>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span>Display</span>
              <select
                value={perPage}
                onChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {PER_PAGE_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            <select
              value={levelFilter}
              onChange={(event) => {
                setLevelFilter(event.target.value);
                setPage(1);
              }}
              className="min-w-[160px] rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">All Levels</option>
              {[1, 2, 3, 4, 5].map((value) => (
                <option key={value} value={value}>
                  Level {value}
                </option>
              ))}
            </select>
            <select
              value={purchaseStatusFilter}
              onChange={(event) => {
                setPurchaseStatusFilter(event.target.value);
                setPage(1);
              }}
              className="min-w-[200px] rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">All Purchase Status</option>
              {statusOptions.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </select>
            <select
              value={accountStatusFilter}
              onChange={(event) => {
                setAccountStatusFilter(event.target.value);
                setPage(1);
              }}
              className="min-w-[200px] rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">All Account Status</option>
              {accountStatusOptions.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </select>
            <select
              value={commissionValidity}
              onChange={(event) => {
                setCommissionValidity(event.target.value);
                setPage(1);
              }}
              className="min-w-[200px] rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">All Commissions</option>
              <option value="valid">Valid Commissions</option>
              <option value="invalid">Invalid Commissions</option>
            </select>
            <div className="flex flex-wrap gap-2 lg:ml-auto">
              <button
                type="button"
                onClick={handleExportExcel}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300"
              >
                <FiDownload />
                Export Excel
              </button>
              {/*
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300"
              >
                <FiDownload />
                Export CSV
              </button>
              */}
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">Payment Date From</span>
              <input
                type="date"
                value={paymentDateFrom}
                onChange={(event) => {
                  setPaymentDateFrom(event.target.value);
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">Payment Date To</span>
              <input
                type="date"
                value={paymentDateTo}
                onChange={(event) => {
                  setPaymentDateTo(event.target.value);
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>
        </div>

        {exportError && (
          <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            {exportError}
          </div>
        )}

        {loadError && (
          <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            {loadError}
          </div>
        )}

        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
          <div className="w-full overflow-x-auto">
            <table className="min-w-[1200px] w-full text-left text-sm text-slate-600">
              <thead className="bg-[linear-gradient(135deg,var(--brand-orange)_0%,var(--brand-orange-dark)_100%)] text-white">
                <tr>
                  {[
                    { key: "name", label: "Name" },
                    { key: "level", label: "Level" },
                    { key: "account_status", label: "User Account Status" },
                    { key: "user_referral_code", label: "User Referral Code" },
                    { key: "inviter_referral_code", label: "Inviter Referral Code" },
                    { key: "payment_date", label: "Date Paid" },
                    { key: "activation_date", label: "Activation Date" },
                    { key: "expiration_date", label: "Expiration Date" },
                    { key: "purchase_status", label: "Purchase Product Status" },
                    { key: "commission_status", label: "Commission Status" },
                    { key: "commission", label: "Commission" },
                  ].map((column) => (
                    <th
                      key={column.key}
                      onClick={() => handleSort(column.key)}
                      className="cursor-pointer px-4 py-3 text-xs font-semibold"
                    >
                      <div className="flex items-center gap-2">
                        {column.label}
                        {sortBy === column.key && (
                          <span className="text-white">{sortDir === "asc" ? "▲" : "▼"}</span>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-xs font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {isLoading ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-sm text-slate-400" colSpan={12}>
                      Loading network records...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-sm text-slate-400" colSpan={12}>
                      No network members found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => {
                    const fullName = [
                      row.first_name,
                      row.middle_name,
                      row.last_name,
                    ]
                      .filter(Boolean)
                      .join(" ");
                    return (
                      <tr key={`${row.purchase_id || row.id}-${index}`} className="border-t border-slate-100">
                        <td className="px-4 py-3 font-semibold text-slate-700">
                          {fullName || "-"}
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-700">
                          {row.level || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`rounded-full px-3 py-1 text-xs font-semibold ${getAccountStatusClass(
                              row.account_status_name
                            )}`}
                          >
                            {row.account_status_name || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3">{row.user_referral_code || "-"}</td>
                        <td className="px-4 py-3">{row.inviter_referral_code || "-"}</td>
                        <td className="px-4 py-3">{formatDateTime(row.payment_date)}</td>
                        <td className="px-4 py-3">{formatDateTime(row.activation_date)}</td>
                        <td className="px-4 py-3">{formatDateTime(row.expiration_date)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${getPurchaseStatusClass(
                          row.purchase_status_name
                        )}`}
                      >
                        {row.purchase_status_name || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          row.commission_status === "Invalid"
                            ? "bg-rose-100 text-rose-700"
                            : row.commission_status === "Valid"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {row.commission_status || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-700">
                      {formatAmount(row.commission_amount)}
                    </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => {
                              setActiveRow(row);
                              setIsViewOpen(true);
                            }}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                          >
                            <FiEye />
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
          <div>
            Showing {rows.length} of {total} entries
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              First
            </button>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page === 1}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Previous
            </button>
            {renderPages()}
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page === totalPages}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Next
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Last
            </button>
          </div>
        </div>
      </div>

      {isViewOpen && activeRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-3xl rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Network
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Member Purchase Details
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Review the full details for this network record.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsViewOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              {[
                {
                  label: "Name",
                  value: [activeRow.first_name, activeRow.middle_name, activeRow.last_name]
                    .filter(Boolean)
                    .join(" "),
                },
                { label: "User Account Status", value: activeRow.account_status_name },
                { label: "User Referral Code", value: activeRow.user_referral_code },
                { label: "Inviter Referral Code", value: activeRow.inviter_referral_code },
                { label: "Payment Date", value: formatDateTime(activeRow.payment_date) },
                { label: "Activation Date", value: formatDateTime(activeRow.activation_date) },
                { label: "Expiration Date", value: formatDateTime(activeRow.expiration_date) },
                { label: "Purchase Status", value: activeRow.purchase_status_name },
                { label: "Level", value: activeRow.level },
                { label: "Commission", value: formatAmount(activeRow.commission_amount) },
                { label: "Commission Status", value: activeRow.commission_status },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
                >
                  <p className="text-xs uppercase tracking-wide text-slate-400">
                    {item.label}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-slate-700">
                    {item.value || "-"}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsViewOpen(false)}
                className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Network;
